import { createSelector } from 'reselect';
import { toSelectionFormat } from '../../../lib/collection.utils';
import { filter, get, find, map, capitalize, uniq } from 'lodash';
import { MODELS, ALL_PERFORMANCES_OPTIONS } from '../../../constants/admin/settings';
import { toDisplayString, msToTime, msToDbTime, timeToMs, msToLongTime, msToHour } from '../../../lib/date.utils';
import { CONTRACT_STATE_FEEDBACK } from '../../../constants/settings';
import { replaceInnerHTML } from '../../../lib/dom.utils';
import { isArtistRole } from './artists';
import { jsonParse } from '../../../lib/string.utils';
import { ALL_ID } from '../../../constants/admin/settings';

// STATE SELECTORS
const formDependencies = state => state.admin.forms.dependencies;
const contextSubject = state => state.admin.context.subject;
const formSelector = state => state.admin.forms[MODELS.CONTRACT];
const template = state => state.admin.forms[MODELS.CONTRACT].templateId;
const templates = state => state.admin.forms.dependencies.templates;

export const editingSelector = createSelector(formSelector, f => !!f.editing);
export const eventOptionsSelector = createSelector(contextSubject, formDependencies, (contextSubject, deps) => {
    const eventsWithContext = contextSubject
        ? filter(deps.events, e => e.organizationId === contextSubject)
        : deps.events;
    return toSelectionFormat(eventsWithContext);
});
export const selectedEventSelector = createSelector(formDependencies, formSelector, (deps, form) => {
    return find(deps.events, d => d.id === form.eventId);
});
export const allFormDataSelector = createSelector(formSelector, f => f);

export const allPerformancesSelector = createSelector(formDependencies, deps => deps.performances || []);

export const scheduleOptionsSelector = createSelector(formDependencies, deps =>
    toSelectionFormat(
        [ALL_PERFORMANCES_OPTIONS].concat(
            (deps.performances || []).map(e => ({
                name: `${(get(e, 'Stage.names') || {})[msToDbTime(e.date)]} | ${toDisplayString(e.startHour)}`,
                id: JSON.stringify({
                    name: `${(get(e, 'Stage.names') || {})[msToDbTime(e.date)]} | ${toDisplayString(e.startHour)}`,
                    id: e.id
                })
            }))
        )
    )
);
export const editorDataSelector = createSelector(formSelector, data => data.data);
export const editorTemplateSelector = createSelector(template, t => t);

export const contractEventIdSelector = createSelector(formSelector, f => f.eventId);
export const contractTransformTriggerSelector = createSelector(formSelector, f => `${f.eventId}${f.artistId}`);
export const templateDataSelector = createSelector(template, templates, (tp, tps) => {
    const selectedTemplate = find(tps, t => t.id === tp);
    return selectedTemplate && selectedTemplate.data;
});
export const templateOptionsSelector = createSelector(formDependencies, deps => toSelectionFormat(deps.templates));
export const templatesSelector = createSelector(templates, t => t);
export const shouldFetchSignersSelector = createSelector(
    formSelector,
    f => f.artistId && f.artistId !== f.fetchingSignersFor && f.artistId !== f.fetchedSignersFor
);
export const signersSelector = createSelector(formSelector, f => f.signers);
export const commentsSelector = createSelector(formSelector, f => f.comment);
export const feedbackSelector = createSelector(formSelector, f => {
    const state = get(f, 'version.state', '');
    const comments = get(f, 'version.comments', {});
    return state === CONTRACT_STATE_FEEDBACK ? comments : {};
});

export const eventNameSelector = createSelector(formSelector, formDependencies, (f, fd) => {
    const event = find(fd.events, e => e.id === f.eventId);
    return event && event.name;
});

export const artistNameSelector = createSelector(formSelector, formDependencies, (f, fd) => {
    const artist = find(fd.artists, e => e.id === f.artistId);
    return artist && artist.name;
});
export const formDataSelector = createSelector(formSelector, f => f);
export const allVersionsSelector = createSelector(formDependencies, d => d.versions);

export const versionsToDropdownItems = (versions = []) => {
    return versions.map(v => ({
        value: v.id,
        label: `${v.isUpload ? 'Uploaded ' : ''}${capitalize(v.state.replace(/_/g, ' '))} version - ${msToTime(
            v.updatedAt,
            true
        )}`
    }));
};
export const allVersionsOptionsSelector = createSelector(formDependencies, d =>
    versionsToDropdownItems(d.versions || [])
);

export const activitiesBuilder = (versions = [], reminders = []) => {
    const combinedActivities = [...(versions || []), ...(reminders || [])];
    return combinedActivities.sort((d1, d2) => new Date(d2.createdAt).getTime() - new Date(d1.createdAt).getTime());
};
export const contractActivitySelector = createSelector(formDependencies, formDataSelector, (d, contract) => {
    return activitiesBuilder(d.versions, contract.reminders);
});

export const selectedVersionSelector = createSelector(formSelector, f => get(f.version, 'id'));
export const isSignedSelector = createSelector(formSelector, f => !!f.signedOn);

export const versionUserSelector = createSelector(allVersionsSelector, selectedVersionSelector, (all, selected) => {
    const sel = find(all, a => a.id === selected);
    return get(sel, 'user.fullName', get(sel, 'anonName', ''));
});

export const isLastVersionSelector = createSelector(
    allVersionsOptionsSelector,
    selectedVersionSelector,
    (all, selected) => selected && all.length && get(all, '[0].value') === selected
);

// DATA PARSING
export function filterSigners(users, orgId, artistId, eventId) {
    const signers = filter(users, user => find(user.roles, r => isArtistRole(r, artistId, orgId, eventId)));
    return map(signers, s => s.email);
}
export const apiDataToState = data => {
    data.performances = (data.performances || []).map(x => JSON.stringify({ name: x.name || '', id: x.id || '' }));
    data.dueDate = (data.dueDate && timeToMs(data.dueDate)) || '';
    // const regexPartners = /<lock(.*?)ckp-rich-text-table(.*?)>(.*?)<\s*\/s*lock>/g;
    data.version = data.version || {};
    data.data = data.version.data || '';
    data.logo = new Buffer((data.version.logo && data.version.logo.data) || []).toString();
    data.signers = '';
    data.name = 'Edit Contract';
    return data;
};

function _getPerformancesData(performances = [], allPerformances = []) {
    const stages = [];
    const dates = [];
    const hours = [];
    performances.forEach(p => {
        try {
            const perfId = jsonParse(p).id;
            const perf = allPerformances.find(p => p.id === perfId);
            if (perfId === ALL_ID) {
                stages.push(
                    uniq(
                        allPerformances.map(perf => `${(get(perf, 'Stage.names') || {})[msToDbTime(perf.date)]}`)
                    ).join(', ')
                );
                dates.push(uniq(allPerformances.map(perf => msToLongTime(perf.date))).join(', '));
                hours.push(
                    uniq(allPerformances.map(perf => `${msToHour(perf.startHour)} - ${msToHour(perf.endHour)}`))
                );
            } else if (perf) {
                stages.push(`${(get(perf, 'Stage.names') || {})[msToDbTime(perf.date)]}`);
                dates.push(msToLongTime(perf.date));
                hours.push(`${msToHour(perf.startHour)} - ${msToHour(perf.endHour)}`);
            }
        } catch (e) {}
    });
    return {
        stages: uniq(stages).join(', '),
        dates: uniq(dates).join(', '),
        hours: uniq(hours).join(', ')
    };
}

export function transformContractText(text, data = {}) {
    const regexEvent = /<\s*ev (.*?)>(.*?)<\s*\/s*ev>/g;
    const regexArtist = /<\s*art (.*?)>(.*?)<\s*\/s*art>/g;
    const regexStage = /<\s*stg (.*?)>(.*?)<\s*\/s*stg>/g;
    const regexDate = /<\s*prfd (.*?)>(.*?)<\s*\/s*prfd>/g;
    const regexHour = /<\s*prfh (.*?)>(.*?)<\s*\/s*prfh>/g;

    const performancesData = _getPerformancesData(data.formData && data.formData.performances, data.allPerformances);

    const firstTransform = text
        .replace(regexEvent, match =>
            replaceInnerHTML('ev', data.eventName ? data.eventName : 'EVENT NAME', match, { keepTags: true })
        )
        .replace(regexArtist, match =>
            replaceInnerHTML('art', data.artistName ? data.artistName : 'ARTIST NAME', match, { keepTags: true })
        )
        .replace(regexStage, match =>
            replaceInnerHTML('art', performancesData.stages ? performancesData.stages : 'PERFORMANCE STAGE', match, {
                keepTags: true
            })
        )
        .replace(regexDate, match =>
            replaceInnerHTML('prfd', performancesData.dates ? performancesData.dates : 'PERFORMANCE DATE', match, {
                keepTags: true
            })
        )
        .replace(regexHour, match =>
            replaceInnerHTML('prfd', performancesData.hours ? performancesData.hours : 'PERFORMANCE DATE', match, {
                keepTags: true
            })
        );
    return firstTransform;
}

export const logoToBase64 = data => {
    data.logo = new Buffer((data.logo && data.logo.data) || []).toString();
    return data;
};

export const canSendContractReminderSelector = createSelector(formSelector, f => {
    return !(f.version.state === 'draft' || f.version.state === 'signed');
});
export const publicUrlSelector = createSelector(formDataSelector, data => {
    return data.publicId ? `URL: ${window.location.origin}/review/${data.publicId} ${data.pinCode ? `\n\n PIN: ${data.pinCode}`: ''}` : false;
});





// WEBPACK FOOTER //
// ./src/selectors/admin/models/contracts.js